import { useQuery, useMutation, QueryClient } from "@tanstack/react-query";
import {
  getMatIntegrations,
  postOnboardingProgression,
  getMatIntegrationConfiguration,
  postMatIntegrationConfiguration,
  postUninstallMatIntegration,
  postMatConfirmation,
} from "@/requests/api/mat";

export const useMatIntegrations = () =>
  useQuery({
    queryKey: ["mat", "integrations"],
    queryFn: getMatIntegrations,
  });

export const useOnboardingProgression = (
  queryClient: QueryClient,
  redirect?: () => void,
) =>
  useMutation({
    mutationFn: postOnboardingProgression,
    onSuccess: data => {
      queryClient.setQueryData(["customer-setup-checklist"], data);
      /**
       * This seems like an anti-pattern, however, we need to allow for
       * redirection on success of the post, even when the onboarding
       * progression component gets unmounted (when the checklist is
       * fully complete).
       */
      if (!!redirect) {
        redirect();
      }
    },
  });

export const useMatIntegrationConfiguration = (
  integration: string,
  isOnboarding: boolean | undefined = undefined,
) =>
  useQuery({
    queryKey: ["mat", "integrations", integration, "configuration"],
    queryFn: () =>
      getMatIntegrationConfiguration({ integration, isOnboarding }),
  });

export const usePostMatIntegrationConfiguration = (
  integration: string,
  isOnboarding: boolean = false,
  queryClient: QueryClient,
) =>
  useMutation({
    mutationFn: postMatIntegrationConfiguration,
    onSuccess: () => {
      /**
       * If we are in onboarding, when the user goes through the
       * authentication step, we client side navigate them to the "config"
       * step. We need to tell react-query to clear the config query key
       * as the next step will need to fetch a new batch of data from the
       * same BE endpoint.
       */
      if (isOnboarding) {
        queryClient.removeQueries({
          queryKey: ["mat", "integrations", integration, "configuration"],
          exact: true,
        });
      }
    },
  });

export const useUninstallMatIntegration = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: postUninstallMatIntegration,
    onSuccess: ({ data }) => {
      queryClient.setQueryData(["mat", "integrations"], data);
    },
  });

export const useMatConfirmation = (
  integration: string,
  queryClient: QueryClient,
) =>
  useMutation({
    mutationFn: postMatConfirmation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mat", "integrations", integration, "configuration"],
        exact: true,
      });
    },
  });
